import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download, Loader2 } from "lucide-react";
import AiPromptGenerator from "@/components/resume-builder/AiPromptGenerator";
import api from "@/lib/api";

const FIELD_CLASS = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400";
const EMPTY_ENTRY = { name: "", issuer: "", date: "", credential_url: "" };

export default function CertificationsSection({ draft, setDraft }) {
  const [importing, setImporting] = useState(false);
  const updateEntry = (idx, field, value) => setDraft({ ...draft, certifications: draft.certifications.map((e, i) => (i === idx ? { ...e, [field]: value } : e)) });
  const removeEntry = (idx) => setDraft({ ...draft, certifications: draft.certifications.filter((_, i) => i !== idx) });
  const addEntry = () => setDraft({ ...draft, certifications: [...draft.certifications, { ...EMPTY_ENTRY }] });

  const importCertifications = async () => {
    setImporting(true);
    try {
      const { data } = await api.get("/resume-builder/import-certifications");
      const existingKeys = new Set(draft.certifications.map((c) => c.credential_url || c.name));
      const newOnes = (data.certifications || []).filter((c) => !existingKeys.has(c.credential_url || c.name));
      if (newOnes.length === 0) {
        toast.success("No new Cloudivo certificates to import -- earn more by completing a 30-Day Challenge track!");
      } else {
        setDraft({ ...draft, certifications: [...draft.certifications, ...newOnes] });
        toast.success(`Imported ${newOnes.length} Cloudivo certificate${newOnes.length === 1 ? "" : "s"}`);
      }
    } catch {
      toast.error("Could not import certifications right now");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="resume-section-certifications">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm font-semibold text-white">Certifications</p>
        <div className="flex items-center gap-3">
          <button onClick={importCertifications} disabled={importing} data-testid="resume-import-certifications-button" className="flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200 disabled:opacity-60">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Import from Cloudivo
          </button>
          <button onClick={addEntry} data-testid="resume-add-certification-button" className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
            <Plus className="w-3.5 h-3.5" /> Add Certification
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {draft.certifications.map((entry, idx) => (
          <div key={idx} className="bg-white/[0.03] rounded-xl p-4" data-testid={`resume-certification-entry-${idx}`}>
            <div className="flex justify-end mb-2">
              <button onClick={() => removeEntry(idx)} data-testid={`resume-remove-certification-${idx}`} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={entry.name} onChange={(e) => updateEntry(idx, "name", e.target.value)} placeholder="Certification Name" data-testid={`resume-certification-name-${idx}`} className={FIELD_CLASS} />
              <input value={entry.issuer} onChange={(e) => updateEntry(idx, "issuer", e.target.value)} placeholder="Issuer (e.g. Salesforce)" data-testid={`resume-certification-issuer-${idx}`} className={FIELD_CLASS} />
              <input value={entry.date} onChange={(e) => updateEntry(idx, "date", e.target.value)} placeholder="Date / Validity" data-testid={`resume-certification-date-${idx}`} className={FIELD_CLASS} />
              <input value={entry.credential_url} onChange={(e) => updateEntry(idx, "credential_url", e.target.value)} placeholder="Credential URL (optional)" data-testid={`resume-certification-url-${idx}`} className={FIELD_CLASS} />
            </div>
          </div>
        ))}
        {draft.certifications.length === 0 && <p className="text-xs text-slate-400">No certifications added yet.</p>}
      </div>
      <div className="mt-3">
        <AiPromptGenerator section="certifications" draft={draft} setDraft={setDraft} />
      </div>
    </div>
  );
}
